import { createSlice, createSelector } from '@reduxjs/toolkit';
// Import all thunks from modular authThunks folder
import {
  registerUser,
  loginUser,
  loginWithGoogle,
  logoutUser,
  updateUserProfile,
  sendPasswordReset,
} from './authThunks/index.js';

const initialState = {
  currentUser: null,
  userData: null,
  loading: false,
  error: null,
  isAuthenticated: false,
  isInitialized: false, // Track if auth state has been initialized
  status: 'idle' // 'idle' | 'loading' | 'succeeded' | 'failed'
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    // Set the authentication state
    setAuthState: (state, { payload }) => {
      state.currentUser = payload?.currentUser || null;
      state.userData = payload?.userData || null;
      state.isAuthenticated = !!payload?.currentUser;
      state.isInitialized = true;
      state.status = payload ? 'succeeded' : 'idle';
    },
    // Clear all auth state on logout
    clearAuthState: (state) => {
      state.currentUser = null;
      state.userData = null;
      state.isAuthenticated = false;
      state.loading = false;
      state.error = null;
      state.status = 'idle';
    },
    // Update user data in the store
    updateUserData: (state, { payload }) => {
      if (state.userData) {
        state.userData = {
          ...state.userData,
          ...payload,
          updatedAt: new Date().toISOString()
        };
      }
    },
    // Clear any auth errors
    clearAuthError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Register User
    builder.addCase(registerUser.pending, (state) => {
      state.loading = true;
      state.status = 'loading';
      state.error = null;
    });
    builder.addCase(registerUser.fulfilled, (state, { payload }) => {
      state.loading = false;
      state.status = 'succeeded';
      state.currentUser = {
        uid: payload.uid,
        email: payload.email,
        emailVerified: payload.emailVerified,
        photoURL: payload.photoURL || ''
      };
      state.userData = payload;
      state.isAuthenticated = true;
      state.isInitialized = true;
    });
    builder.addCase(registerUser.rejected, (state, { payload }) => {
      state.loading = false;
      state.status = 'failed';
      state.error = payload;
    });

    // Login User
    builder.addCase(loginUser.pending, (state) => {
      state.loading = true;
      state.status = 'loading';
      state.error = null;
    });
    builder.addCase(loginUser.fulfilled, (state, { payload }) => {
      state.loading = false;
      state.status = 'succeeded';
      state.currentUser = {
        uid: payload.uid,
        email: payload.email,
        emailVerified: payload.emailVerified,
        photoURL: payload.photoURL || ''
      };
      state.userData = payload;
      state.isAuthenticated = true;
      state.isInitialized = true;
    });
    builder.addCase(loginUser.rejected, (state, { payload }) => {
      state.loading = false;
      state.status = 'failed';
      state.error = payload;
    });

    // Google Login
    builder.addCase(loginWithGoogle.pending, (state) => {
      state.loading = true;
      state.status = 'loading';
      state.error = null;
    });
    builder.addCase(loginWithGoogle.fulfilled, (state, { payload }) => {
      state.loading = false;
      state.status = 'succeeded';
      state.currentUser = {
        uid: payload.user.uid,
        email: payload.user.email,
        emailVerified: payload.user.emailVerified,
        photoURL: payload.user.photoURL || payload.userData?.photoURL || ''
      };
      state.userData = payload.userData || payload.user;
      state.isAuthenticated = true;
      state.isInitialized = true;
    });
    builder.addCase(loginWithGoogle.rejected, (state, { payload }) => {
      state.loading = false;
      state.status = 'failed';
      state.error = payload;
    });

    // Logout
    builder.addCase(logoutUser.fulfilled, (state) => {
      state.currentUser = null;
      state.userData = null;
      state.isAuthenticated = false;
      state.loading = false;
      state.status = 'idle';
      state.error = null;
    });

    // Update Profile
    // NOTE: We intentionally do NOT set state.loading = true here
    // Setting loading to true would hide the entire page via AuthContext
    // Instead, the modal uses its own local isSubmitting state for the button
    builder.addCase(updateUserProfile.pending, (state) => {
      // Don't set global loading - keep page visible during profile update
      state.status = 'loading';
      state.error = null;
    });
    builder.addCase(updateUserProfile.fulfilled, (state, { payload }) => {
      state.status = 'succeeded';
      state.userData = {
        ...state.userData,
        ...payload,
        updatedAt: new Date().toISOString()
      };

      // Update current user email if it was changed
      if (payload.email) {
        state.currentUser = {
          ...state.currentUser,
          email: payload.email
        };
      }
    });
    builder.addCase(updateUserProfile.rejected, (state, { payload }) => {
      state.status = 'failed';
      state.error = payload;
    });

    // Password Reset
    builder.addCase(sendPasswordReset.pending, (state) => {
      state.loading = true;
      state.status = 'loading';
      state.error = null;
    });
    builder.addCase(sendPasswordReset.fulfilled, (state) => {
      state.loading = false;
      state.status = 'succeeded';
    });
    builder.addCase(sendPasswordReset.rejected, (state, { payload }) => {
      state.loading = false;
      state.status = 'failed';
      state.error = payload;
    });
  },
});

// Export actions
export const {
  setAuthState,
  clearAuthState,
  clearAuthError,
  updateUserData
} = authSlice.actions;

// Export selectors
export const selectCurrentUser = (state) => state.auth.currentUser;
export const selectUserData = (state) => state.auth.userData;
export const selectIsAuthenticated = (state) => state.auth.isAuthenticated;
export const selectAuthLoading = (state) => state.auth.loading;
export const selectAuthError = (state) => state.auth.error;
export const selectAuthStatus = (state) => state.auth.status;
export const selectIsInitialized = (state) => state.auth.isInitialized;

// Memoized resolved selectors
// NOTE: userData is already normalized by AuthContext before dispatching to Redux,
// so we just return it directly. No need to re-normalize.

// Stable fallback constant (defined outside selector to prevent new references)
const EMPTY_USER_DATA = Object.freeze({
  id: null,
  uid: null,
  email: null,
  username: '',
  displayName: 'User',
  photoURL: '',
  coverPhotoURL: '',
  userProfession: '',
  location: '',
  vision: '',
  emailPublic: false,
  locationPublic: false,
  following: [],
  followers: [],
  followingCount: 0,
  followersCount: 0,
  lastUpdated: ''
});

export const selectResolvedUserData = createSelector(
  [selectUserData],
  (userData) => userData || EMPTY_USER_DATA
);

export const selectResolvedDisplayName = createSelector(
  [selectResolvedUserData],
  (resolvedUserData) => resolvedUserData?.displayName || 'User'
);

export const selectResolvedPhotoURL = createSelector(
  [selectResolvedUserData],
  (resolvedUserData) => resolvedUserData?.photoURL || ''
);

// Re-export all thunks for backward compatibility
export {
  registerUser,
  loginUser,
  loginWithGoogle,
  logoutUser,
  updateUserProfile,
  sendPasswordReset,
};

export default authSlice.reducer; 